import { type SetStateAction, type Dispatch, useState, useEffect } from 'react';
import { X, TriangleAlert, Trash2 } from 'lucide-react';
import { Button, Input } from '~/components/ui';
import {
  useUpsertInventory,
  useGetBuyerProductInventory,
} from '~/api/inventory.tsx';
import { useUpsertBuyerInventoryConfiguration } from '~/api/buyerInventoryConfiguration.tsx';
import { useTranslation, Trans } from 'react-i18next';
import { isNonNegativeNumber } from '~/utils/inventory.tsx';
import type { Inventory } from '~/graphql/graphql';
import toast from 'react-hot-toast';
import { extractGraphQLErrorMessages } from '~/utils';

type EditInventoryModalProps = {
  readonly setShowEditInventory: Dispatch<SetStateAction<boolean>>;
  readonly onSaved?: () => void; // new optional callback
  readonly inventory: Inventory;
};

export function EditInventoryModal({
  setShowEditInventory,
  inventory,
  onSaved,
}: EditInventoryModalProps) {
  const { data: buyerProductInventory, refetch } = useGetBuyerProductInventory(
    inventory.id
  );
  const { t } = useTranslation('inventory');
  const productCode =
    inventory.platformProductsLength.platformProduct.code ?? '';
  const productName =
    inventory.platformProductsLength.platformProduct.name ?? '';
  const lengthValue =
    inventory.platformProductsLength.length.displayValue ?? '';
  const locationName = inventory.location.name ?? '';
  const locationCity = inventory.location.city ?? '';
  const locationRegion = inventory.location.region ?? '';
  const platformProductsLengthId = inventory.platformProductsLength.id;
  const locationId = inventory.location.id;
  const species =
    inventory.platformProductsLength.platformProduct.properties!.species
      ?.name ?? '';
  const initialStock = inventory.quantity.value ?? 0;
  const initialUsage =
    buyerProductInventory?.buyerProductInventory?.buyerInventoryConfiguration
      ?.usageRatePerWeek.value ?? 0;
  const initialUrgentWeeks =
    buyerProductInventory?.buyerProductInventory?.buyerInventoryConfiguration
      ?.weeksCoveredBeforeUrgent ?? 0;
  const initialMonitorWeeks =
    buyerProductInventory?.buyerProductInventory?.buyerInventoryConfiguration
      ?.weeksCoveredBeforeMonitor ?? 0;
  const initialTargetWeeks =
    buyerProductInventory?.buyerProductInventory?.buyerInventoryConfiguration
      ?.targetWeeksCovered ?? 0;
  const [currentStock, setCurrentStock] = useState<number | ''>(initialStock);
  const [avgWeeklyConsumption, setAvgWeeklyConsumption] = useState<number | ''>(
    initialUsage
  );
  const [urgentWeeks, setUrgentWeeks] = useState<number | ''>(
    initialUrgentWeeks
  );
  const [monitorWeeks, setMonitorWeeks] = useState<number | ''>(
    initialMonitorWeeks
  );
  const [targetWeeks, setTargetWeeks] = useState<number | ''>(
    initialTargetWeeks
  );

  // refetch latest inventory when modal mounts / inventory id changes
  useEffect(() => {
    if (!inventory.id) return;
    // call refetch if available
    void refetch();
  }, [inventory.id, refetch]);

  useEffect(() => {
    // projection shape may vary; try query result first then fallback to passed `inventory`
    const queryInv = buyerProductInventory?.buyerProductInventory;
    const cfg =
      queryInv?.buyerInventoryConfiguration ??
      inventory?.buyerInventoryConfiguration;

    if (!cfg) return;

    if (cfg.usageRatePerWeek.value != null) {
      setAvgWeeklyConsumption(cfg.usageRatePerWeek.value);
    }
    if (cfg.weeksCoveredBeforeUrgent != null) {
      setUrgentWeeks(cfg.weeksCoveredBeforeUrgent);
    }
    if (cfg.weeksCoveredBeforeMonitor != null) {
      setMonitorWeeks(cfg.weeksCoveredBeforeMonitor);
    }
    if (cfg.targetWeeksCovered != null) {
      setTargetWeeks(cfg.targetWeeksCovered);
    }
  }, [buyerProductInventory, inventory]);

  const { mutate: mutateInventory } = useUpsertInventory();
  const { mutate: mutateBuyerInventoryConfiguration } =
    useUpsertBuyerInventoryConfiguration();
  // Helper to check non-negative numeric inputs

  const canSubmit =
    isNonNegativeNumber(currentStock) &&
    isNonNegativeNumber(avgWeeklyConsumption) &&
    isNonNegativeNumber(urgentWeeks) &&
    isNonNegativeNumber(monitorWeeks) &&
    isNonNegativeNumber(targetWeeks);

  const isValid = () => {
    if (!platformProductsLengthId || !locationId) {
      toast.error(t('inventory.validation.something_went_wrong'));
      return false;
    }

    if (Number(monitorWeeks) <= Number(urgentWeeks)) {
      toast.error(t('inventory.validation.monitor_weeks_less_than_urgent'));
      return false;
    }

    if (Number(targetWeeks) < Number(monitorWeeks)) {
      toast.error(t('inventory.validation.target_weeks_less_than_monitor'));
      return false;
    }

    const hasDecimalUrgent = Number(urgentWeeks) % 1 !== 0;
    const hasDecimalMonitor = Number(monitorWeeks) % 1 !== 0;
    const hasDecimalTarget = Number(targetWeeks) % 1 !== 0;

    if (hasDecimalUrgent || hasDecimalMonitor || hasDecimalTarget) {
      toast.error(t('inventory.validation.no_partial_weeks'));
      return false;
    }

    return true;
  };

  const onSave = () => {
    if (!isValid()) return;

    mutateInventory(
      {
        platformProductsLengthId,
        locationId,
        quantity: { value: Number(currentStock), unit: 'MBF' },
      },
      {
        onSuccess: () => {
          mutateBuyerInventoryConfiguration(
            {
              platformProductsLengthId,
              locationId,
              usageRatePerWeek: {
                value: Number(avgWeeklyConsumption),
                unit: 'MBF',
              },
              weeksCoveredBeforeUrgent: Number(urgentWeeks),
              weeksCoveredBeforeMonitor: Number(monitorWeeks),
              targetWeeksCovered: Number(targetWeeks),
            },
            {
              onSuccess: () => {
                setShowEditInventory(false);
                toast.success(t('inventory.update_success'));
                onSaved?.(); // notify parent to refetch/refresh
              },
              onError: (e) =>
                toast.error(
                  extractGraphQLErrorMessages(e).join(', ') ||
                    t('inventory.validation.something_went_wrong')
                ),
            }
          );
        },
      }
    );
  };

  const weeksCovered =
    Number(avgWeeklyConsumption) > 0
      ? Number(currentStock) / Number(avgWeeklyConsumption)
      : Infinity;

  const statusLabel = (() => {
    const healthy = t('inventory.healthy');
    const urgent = t('inventory.urgent');
    const monitor = t('inventory.monitor');

    if (!Number.isFinite(weeksCovered)) return healthy;
    if (weeksCovered <= Number(urgentWeeks || 0)) return urgent;
    if (weeksCovered <= Number(monitorWeeks || 0)) return monitor;
    return healthy;
  })();

  return (
    <div className="bg-neut-0 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="px-6 py-4 border-b border-neut-200 flex items-center justify-between sticky top-0 bg-neut-0">
        <div>
          <h2 className="text-lg font-semibold text-neut-900">
            {t('inventory.edit_title')}
          </h2>
          <p className="text-sm text-neut-500">
            {productName} · {locationName}
          </p>
        </div>
        <button
          className="p-2 hover:bg-neut-100 rounded-lg transition-colors"
          onClick={() => setShowEditInventory(false)}
          aria-label="Close"
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      <div className="p-6 space-y-6">
        <div className="bg-neut-50 rounded-lg p-4 space-y-2">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-neut-600">
                {t('inventory.product_code')}
              </span>
              <span className="ml-2 font-medium text-neut-900">
                {productCode}
              </span>
            </div>
            <div>
              <span className="text-neut-600">{t('inventory.species')}:</span>
              <span className="ml-2 font-medium text-neut-900">{species}</span>
            </div>
            <div>
              <span className="text-neut-600">{t('inventory.length')}:</span>
              <span className="ml-2 font-medium text-neut-900">
                {lengthValue}
              </span>
            </div>
            <div>
              <span className="text-neut-600">{t('inventory.location')}:</span>
              <span className="ml-2 font-medium text-neut-900">
                {locationCity}, {locationRegion}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-neut-900">
            {t('inventory.level')}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                name="stock"
                min={0}
                label={t('inventory.stock')}
                type="number"
                step="0.01"
                data-testid="edit-stock"
                value={currentStock}
                onChange={(_, value) =>
                  setCurrentStock(value === '' ? '' : Number(value))
                }
              />
              <p className="text-xs text-neut-500 mt-1">MBF</p>
            </div>

            <div>
              <Input
                name="usageRatePerWeek"
                min={0}
                label={t('inventory.usage_rate')}
                type="number"
                step="0.01"
                data-testid="edit-usage"
                value={avgWeeklyConsumption}
                onChange={(_, value) =>
                  setAvgWeeklyConsumption(value === '' ? '' : Number(value))
                }
              />
              <p className="text-xs text-neut-500 mt-1">
                Average amount consumed per week
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-neut-900">
            {t('inventory.status')} {t('inventory.thresholds')}
          </h3>
          <p className="text-xs text-neut-600">
            {t('inventory.threshold_description')}
          </p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Input
                min={0}
                label={t('inventory.urgent_threshold')}
                type="number"
                name="urgent-threshold"
                value={urgentWeeks}
                onChange={(_, value) =>
                  setUrgentWeeks(value === '' ? '' : Number(value))
                }
              />

              <p className="text-xs text-neut-500 mt-1">
                <Trans
                  i18nKey="inventory.status_becomes_urgent"
                  components={{
                    strong: <span className="font-semibold text-error-600" />,
                    lt: <>{'<'}</>,
                  }}
                  ns="inventory"
                />
              </p>
            </div>

            <div>
              <Input
                min={0}
                label={t('inventory.medium_threshold')}
                type="number"
                name="medium-threshold"
                value={monitorWeeks}
                onChange={(_, value) =>
                  setMonitorWeeks(value === '' ? '' : Number(value))
                }
              />
              <p className="text-xs text-neut-500 mt-1">
                <Trans
                  i18nKey="inventory.status_becomes_monitor"
                  ns="inventory"
                  components={{
                    strong: <span className="font-semibold text-warning-600" />,
                    lt: <>{'<'}</>,
                  }}
                />
              </p>
            </div>

            <div>
              <Input
                label={t('inventory.target_threshold')}
                type="number"
                min={0}
                name="target-threshold"
                value={targetWeeks}
                onChange={(_, value) => setTargetWeeks(Number(value))}
              />
              <p className="text-xs text-neut-500 mt-1">
                <Trans
                  i18nKey="inventory.status_is_healthy"
                  ns="inventory"
                  components={{
                    strong: <span className="font-semibold text-success-600" />,
                    ge: <>{'≥'}</>,
                  }}
                />
              </p>
            </div>
          </div>
        </div>

        <div className="bg-status-info-50 border border-status-info-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <TriangleAlert
              size={18}
              className="text-status-info-600 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="text-sm text-status-info-800">
              <p className="font-medium mb-1">Current Status</p>
              <div className="flex items-center gap-2 text-xs">
                <span>
                  {t('inventory.cover')}{' '}
                  <strong>
                    {Number.isFinite(weeksCovered)
                      ? weeksCovered.toFixed(2)
                      : '∞'}
                  </strong>
                </span>
                <span>•</span>
                <span className="px-2 py-0.5 rounded-full font-medium bg-success-100 text-success-700">
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-neut-200 flex items-center justify-between bg-neut-50 sticky bottom-0">
        <button
          className="px-4 py-2 border border-error-300 text-error-600 rounded-lg hover:bg-warning-50 transition-colors text-sm font-medium flex items-center gap-2"
          aria-label="Delete"
          type="button"
        >
          <Trash2 size={16} aria-hidden="true" />
          {t('inventory.delete')}
        </button>

        <div className="flex items-center gap-3">
          <Button
            variant={'secondary'}
            width={'5rem'}
            onClick={() => setShowEditInventory(false)}
          >
            {t('inventory.cancel')}
          </Button>
          <Button onClick={onSave} width={'8rem'} disabled={!canSubmit}>
            {t('inventory.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
